import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Badge } from"@/components/ui/badge";
import { Plus, Users, Edit, Trash2, Building2, Phone, Mail, Briefcase } from"lucide-react";
import { toast } from"sonner";

const PartnersPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [partners, setPartners] = useState([]);
 const [loading, setLoading] = useState(false);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [editMode, setEditMode] = useState(false);
 const [editingPartnerId, setEditingPartnerId] = useState(null);
 const [formData, setFormData] = useState({
 company_name:"",
 contact_person:"",
 email:"",
 phone:"",
 specialization:"",
 notes:""
 });

 useEffect(() => {
 loadPartners();
 }, []);

 const loadPartners = async () => {
 setLoading(true);
 try {
 const response = await axios.get("/partners");
 setPartners(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const openAddDialog = () => {
 setEditMode(false);
 setEditingPartnerId(null);
 setFormData({
 company_name:"",
 contact_person:"",
 email:"",
 phone:"",
 specialization:"",
 notes:""
 });
 setDialogOpen(true);
 };

 const openEditDialog = (partner) => {
 setEditMode(true);
 setEditingPartnerId(partner.id);
 setFormData({
 company_name: partner.company_name,
 contact_person: partner.contact_person,
 email: partner.email,
 phone: partner.phone,
 specialization: partner.specialization ||"",
 notes: partner.notes ||""
 });
 setDialogOpen(true);
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 if (editMode) {
 await axios.patch(`/partners/${editingPartnerId}`, formData);
 toast.success(t("messages.updateSuccess"));
 } else {
 await axios.post("/partners", formData);
 toast.success(t("messages.saveSuccess"));
 }
 
 setDialogOpen(false);
 loadPartners();
 } catch (error) {
 toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'add'} partner`);
 }
 };

 const deletePartner = async (partnerId) => {
 if (!window.confirm("Are you sure you want to delete this partner? This will only work if they are not assigned to any projects.")) return;
 
 try {
 await axios.delete(`/partners/${partnerId}`);
 toast.success(t("messages.deleteSuccess"));
 loadPartners();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to delete partner");
 }
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {t('partners.title')}
 </h1>
 <p className="text-slate-500 mt-1">{t('partners.subtitle')}</p>
 </div>
 <Button onClick={openAddDialog}>
 <Plus className="w-4 h-4 mr-2"/>
 {t('partners.addPartner')}
 </Button>
 </div>

 {/* Partners List */}
 <Card>
 <CardHeader>
 <CardTitle>{t('partners.allPartners')}</CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
 ) : partners.length === 0 ? (
 <div className="text-center py-12">
 <Users className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-500">{t('partners.noPartners')}</p>
 <p className="text-sm text-slate-400 mt-1">{t('partners.addPartnersHint')}</p>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {partners.map((partner) => (
 <div 
 key={partner.id} 
 className="border rounded-sm p-4 hover:shadow-md transition-shadow bg-white"
 >
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-blue-600"/>
 <h3 className="font-semibold text-slate-900 text-lg">{partner.company_name}</h3>
 </div>
 </div>
 
 <div className="space-y-2 text-sm">
 <div className="flex items-center gap-2 text-[#334155]">
 <Users className="w-4 h-4 text-slate-400"/>
 <span>{partner.contact_person}</span>
 </div>
 
 <div className="flex items-center gap-2 text-[#334155]">
 <Mail className="w-4 h-4 text-slate-400"/>
 <a href={`mailto:${partner.email}`} className="text-blue-600 hover:underline">
 {partner.email}
 </a>
 </div>
 
 <div className="flex items-center gap-2 text-[#334155]">
 <Phone className="w-4 h-4 text-slate-400"/>
 <a href={`tel:${partner.phone}`} className="hover:text-blue-600">
 {partner.phone}
 </a>
 </div>
 
 {partner.specialization && (
 <div className="flex items-center gap-2 text-[#334155]">
 <Briefcase className="w-4 h-4 text-slate-400"/>
 <Badge variant="outline"className="text-xs">
 {partner.specialization}
 </Badge>
 </div>
 )}
 
 {partner.notes && (
 <div className="mt-2 p-2 bg-white/50 rounded text-xs text-slate-600">
 {partner.notes}
 </div>
 )}
 </div>
 
 <div className="flex gap-2 mt-4 pt-3 border-t">
 <Button
 size="sm"
 variant="outline"
 onClick={() => openEditDialog(partner)}
 className="flex-1"
 >
 <Edit className="w-3 h-3 mr-1"/>
 Edit
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => deletePartner(partner.id)}
 className="text-red-600 hover:bg-red-50/80 hover:text-red-700"
 >
 <Trash2 className="w-3 h-3 mr-1"/>
 Delete
 </Button>
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Add/Edit Partner Dialog */}
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent className="max-w-lg">
 <DialogHeader>
 <DialogTitle>{editMode ?"Edit Partner":"Add New Partner"}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="company_name">Company Name *</Label>
 <Input
 id="company_name"
 value={formData.company_name}
 onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
 placeholder="e.g., ABC Construction Ltd."
 required
 />
 </div>
 
 <div>
 <Label htmlFor="contact_person">Contact Person *</Label>
 <Input
 id="contact_person"
 value={formData.contact_person}
 onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
 placeholder="e.g., John Doe"
 required
 />
 </div>
 
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="email">Email *</Label>
 <Input
 id="email"
 type="email"
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 placeholder="contact@company.com"
 required
 />
 </div>
 <div>
 <Label htmlFor="phone">Phone *</Label>
 <Input
 id="phone"
 value={formData.phone}
 onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
 placeholder="+1234567890"
 required
 />
 </div>
 </div>
 
 <div>
 <Label htmlFor="specialization">Specialization / Services</Label>
 <Input
 id="specialization"
 value={formData.specialization}
 onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
 placeholder="e.g., Concrete, Electrical, Plumbing"
 />
 <p className="text-[11px] text-slate-500 mt-1">
 Comma-separated list of services this company provides
 </p>
 </div>
 
 <div>
 <Label htmlFor="notes">{t("common.notes")}</Label>
 <Textarea
 id="notes"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 placeholder="Any additional information about this partner..."
 rows={3}
 />
 </div>
 
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">
 {editMode ?"Update Partner":"Add Partner"}
 </Button>
 <Button 
 type="button"
 variant="outline"
 onClick={() => setDialogOpen(false)} 
 className="flex-1"
 >
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default PartnersPage;
